import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Save,
  Info,
  Tags,
  DollarSign,
  Package,
  Globe,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Pencil,
  ExternalLink,
  RotateCcw,
} from "lucide-react";
import {
  getAllCountriesData,
  updateAllCountriesData,
  refreshAllCountriesData,
} from "../../../../features/productSlice";
import Api from "../../../../util/API";

const ACCENT = "#D2042D";
const cx = (...arr) => arr.filter(Boolean).join(" ");
const STOREFRONT_BASE_URL = "https://cherrfy.com";
const normalizeCategoryPath = (value) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => String(entry || "").trim())
    .filter(Boolean)
    .slice(0, 2);

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const SectionHead = ({ title, subtitle, right }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="h-4 w-[3px] rounded-full" style={{ background: ACCENT }} />
        <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">{title}</h2>
      </div>
      {subtitle ? <p className="mt-1 text-xs text-slate-500 line-clamp-2">{subtitle}</p> : null}
    </div>
    {right ? <div className="w-full sm:w-auto sm:shrink-0">{right}</div> : null}
  </div>
);

const Chip = ({ icon: Icon, children, tone = "neutral" }) => {
  const toneMap = {
    neutral: "border-slate-200 bg-white text-slate-700",
    good: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warn: "border-amber-200 bg-amber-50 text-amber-700",
    bad: "border-rose-200 bg-rose-50 text-rose-700",
  };
  return (
    <span className={cx("inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs", toneMap[tone])}>
      {Icon ? <Icon size={14} /> : null}
      <span className="font-semibold">{children}</span>
    </span>
  );
};

const Label = ({ children }) => (
  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">{children}</p>
);

const Input = (props) => (
  <input
    {...props}
    className={cx(
      "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900",
      "focus:outline-none focus:ring-2 focus:ring-slate-200",
      props.className
    )}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={cx(
      "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900",
      "focus:outline-none focus:ring-2 focus:ring-slate-200",
      props.className
    )}
  />
);

const Select = (props) => (
  <select
    {...props}
    className={cx(
      "rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900",
      "focus:outline-none focus:ring-2 focus:ring-slate-200",
      props.className
    )}
  />
);

export default function AliExpressEdit() {
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const navigate = useNavigate();

  const [productId, setProductId] = useState(searchParams.get("productId") || id || "");
  const [activeTab, setActiveTab] = useState("overview"); // overview | info | pricing
  const [editingData, setEditingData] = useState({});
  const [selectedCountry, setSelectedCountry] = useState("US");

  const [isEditing, setIsEditing] = useState(false);
  const [editingSkuIndex, setEditingSkuIndex] = useState(null);
  const [applyToAllCountries, setApplyToAllCountries] = useState(false);
  const [skuPricing, setSkuPricing] = useState({}); // { [countryCode]: { [sku_id]: overrideRow } }
  const [isSkuPricingLoading, setIsSkuPricingLoading] = useState(false);
  const [markupByCountry, setMarkupByCountry] = useState({});
  const [isMarkupLoading, setIsMarkupLoading] = useState(false);
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const countryMenuRef = useRef(null);
  const [countryNameMap, setCountryNameMap] = useState({});
  const [categories, setCategories] = useState([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [categoryPath, setCategoryPath] = useState([]);
  const [categoryDirty, setCategoryDirty] = useState(false);
  const [isCategorySaving, setIsCategorySaving] = useState(false);

  const dispatch = useDispatch();
  const productsState = useSelector((state) => state.products);
  const selectedProduct = useMemo(
    () => productsState?.selectedProduct || null,
    [productsState?.selectedProduct]
  );
  const status = productsState?.status || "idle";
  const error = productsState?.error || "";

  // Load product data
  useEffect(() => {
    if (productId) fetchProductData({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    if (activeTab === "pricing") {
      setApplyToAllCountries(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isCountryMenuOpen) return;

    const handlePointer = (event) => {
      if (!countryMenuRef.current) return;
      if (!countryMenuRef.current.contains(event.target)) {
        setIsCountryMenuOpen(false);
      }
    };

    const handleKey = (event) => {
      if (event.key === "Escape") {
        setIsCountryMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isCountryMenuOpen]);

  useEffect(() => {
    let cancelled = false;

    const loadCountryNames = async () => {
      try {
        const res = await Api.get("/settings/countries/all");
        const list = Array.isArray(res?.data?.countries) ? res.data.countries : [];
        const map = list.reduce((acc, row) => {
          const cc = String(row?.countryCode || "").trim().toUpperCase();
          if (cc) acc[cc] = row?.countryName || cc;
          return acc;
        }, {});
        if (!cancelled) setCountryNameMap(map);
      } catch {
        if (!cancelled) setCountryNameMap({});
      }
    };

    loadCountryNames();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      try {
        setIsCategoriesLoading(true);
        const res = await Api.get("/category");
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) setCategories(list);
      } catch {
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setIsCategoriesLoading(false);
      }
    };

    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  const getCountryLabel = (code) => {
    const cc = String(code || "").trim().toUpperCase();
    return countryNameMap?.[cc] || cc;
  };

  const availableCountries = useMemo(() => {
    const list = Object.keys(editingData || {})
      .map((c) => String(c || "").trim().toUpperCase())
      .filter(Boolean);
    return Array.from(new Set(list));
  }, [editingData]);

  const safeCountry = useMemo(() => {
    if (selectedCountry && editingData?.[selectedCountry]) return selectedCountry;
    const first = Object.keys(editingData || {})[0];
    return first || "US";
  }, [selectedCountry, editingData]);

  const sortedAvailableCountries = useMemo(() => {
    return [...availableCountries].sort((a, b) =>
      (countryNameMap?.[a] || a).localeCompare(countryNameMap?.[b] || b)
    );
  }, [availableCountries, countryNameMap]);

  // Load SKU pricing overrides for the selected country
  useEffect(() => {
    if (!productId) return;
    const cc = String(safeCountry || "US").toUpperCase();
    if (!cc) return;
    let cancelled = false;

    (async () => {
      try {
        setIsSkuPricingLoading(true);
        const res = await Api.get(`/sku-pricing/${productId}?country=${cc}`);
        const rows = res?.data?.data;
        const map = Array.isArray(rows)
          ? rows.reduce((acc, r) => {
              const key = String(r?.sku_id || "").trim();
              if (key) acc[key] = r;
              return acc;
            }, {})
          : {};

        if (!cancelled) {
          setSkuPricing((prev) => ({
            ...prev,
            [cc]: map,
          }));
        }
      } catch {
        // keep silent; pricing overrides are optional
      } finally {
        if (!cancelled) setIsSkuPricingLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [productId, safeCountry]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setIsMarkupLoading(true);
        const res = await Api.get("/settings/markup-table");
        const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
        const map = rows.reduce((acc, row) => {
          const cc = String(row?.countryCode || "").trim().toUpperCase();
          const percent = Number(row?.percent);
          if (!cc) return acc;
          acc[cc] = Number.isFinite(percent) ? percent : 0;
          return acc;
        }, {});

        if (!cancelled) setMarkupByCountry(map);
      } catch {
        if (!cancelled) setMarkupByCountry({});
      } finally {
        if (!cancelled) setIsMarkupLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Sync local editing state from store
  useEffect(() => {
    if (selectedProduct?.ali_data) setEditingData(selectedProduct.ali_data);
    const nextCategoryId = selectedProduct?.categoryId || selectedProduct?.category?._id || "";
    setCategoryId(nextCategoryId ? String(nextCategoryId) : "");
    setCategoryPath(normalizeCategoryPath(selectedProduct?.categoryPath));
    setCategoryDirty(false);
  }, [selectedProduct]);

  const selectedCategory = useMemo(() => {
    return (categories || []).find((row) => String(row?._id || "") === String(categoryId));
  }, [categories, categoryId]);

  const level1Options = useMemo(() => {
    return Array.isArray(selectedCategory?.subcategories) ? selectedCategory.subcategories : [];
  }, [selectedCategory]);

  const level1Value = categoryPath?.[0] || "";
  const selectedLevel1 = useMemo(
    () => level1Options.find((row) => String(row?.name || "") === String(level1Value)),
    [level1Options, level1Value]
  );
  const level2Options = useMemo(() => {
    return Array.isArray(selectedLevel1?.subcategories) ? selectedLevel1.subcategories : [];
  }, [selectedLevel1]);
  const level2Value = categoryPath?.[1] || "";

  const selectedCategoryName = useMemo(() => {
    if (!categoryId) return "Unassigned";
    const cat = (categories || []).find((row) => String(row?._id || "") === String(categoryId));
    const base = cat?.name || "Assigned";
    const path = (Array.isArray(categoryPath) ? categoryPath : []).filter(Boolean).join(" > ");
    return path ? `${base} > ${path}` : base;
  }, [categories, categoryId, categoryPath]);

  const tabs = useMemo(
    () => [
      { id: "overview", label: "Overview", icon: Info },
      { id: "info", label: "Information", icon: Tags },
      { id: "pricing", label: "Pricing", icon: DollarSign },
    ],
    []
  );

  const stats = useMemo(() => {
    const countriesCount = Object.keys(editingData || {}).length;

    const skuCount = Object.values(editingData || {}).reduce((total, country) => {
      const n = country?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.length || 0;
      return total + n;
    }, 0);

    const imagesCount = Object.values(editingData || {}).reduce((total, country) => {
      const urls = country?.ae_multimedia_info_dto?.image_urls || "";
      const n = urls ? urls.split(";").filter(Boolean).length : 0;
      return total + n;
    }, 0);

    return { countriesCount, skuCount, imagesCount };
  }, [editingData]);

  const fetchProductData = async (optsOrEvent) => {
    const isEvent = Boolean(optsOrEvent && typeof optsOrEvent.preventDefault === "function");
    const silent = Boolean(!isEvent && optsOrEvent?.silent);
    if (isEvent) optsOrEvent.preventDefault();
    if (!productId.trim()) {
      if (!silent) toast.error("Please enter a valid Product ID.");
      return;
    }
    try {
      const res = await dispatch(getAllCountriesData(productId));
      if (res?.error) {
        if (!silent) toast.error(res.error.message || "Failed to fetch product data.");
        return;
      }
      const ali = res?.payload?.data?.ali_data || {};
      setEditingData(ali);
      const nextCategoryId = res?.payload?.data?.categoryId || res?.payload?.data?.category?._id || "";
      setCategoryId(nextCategoryId ? String(nextCategoryId) : "");
      setCategoryPath(normalizeCategoryPath(res?.payload?.data?.categoryPath));
      setCategoryDirty(false);
      if (!silent) toast.success("Product data loaded successfully.");
    } catch {
      if (!silent) toast.error("Error fetching product data.");
    }
  };

  const handleRefreshData = async () => {
    if (!productId.trim()) {
      toast.error("Please enter a Product ID first.");
      return;
    }
    try {
      const res = await dispatch(refreshAllCountriesData(productId));
      if (res?.error) {
        toast.error(res.error.message || "Failed to refresh data.");
        return;
      }
      const ali = res?.payload?.data?.ali_data || {};
      setEditingData(ali);
      const nextCategoryId = res?.payload?.data?.categoryId || res?.payload?.data?.category?._id || "";
      setCategoryId(nextCategoryId ? String(nextCategoryId) : "");
      setCategoryPath(normalizeCategoryPath(res?.payload?.data?.categoryPath));
      setCategoryDirty(false);
      toast.success("Data refreshed successfully from AliExpress.");
    } catch {
      toast.error("Error refreshing data.");
    }
  };

  const handleSaveChanges = async () => {
    if (!productId.trim()) {
      toast.error("Please enter a Product ID first.");
      return;
    }
    try {
      const res = await dispatch(updateAllCountriesData({
        productId,
        ali_data: editingData,
        categoryId: categoryId || null,
        categoryPath: normalizeCategoryPath(categoryPath),
      }));
      if (res?.error) {
        toast.error(res.error.message || "Failed to save changes.");
        return;
      }
      toast.success("All countries data updated successfully.");
      setIsEditing(false);
      setEditingSkuIndex(null);
      setCategoryDirty(false);
    } catch {
      toast.error("Error saving changes.");
    }
  };

  const handleCategorySelectionChange = (value) => {
    setCategoryId(value);
    setCategoryPath([]);
    setCategoryDirty(true);
  };

  const handleCategoryPathLevelChange = (level, value) => {
    setCategoryPath((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      if (!value) return next.slice(0, level);
      next[level] = String(value || "").trim();
      return next.slice(0, level + 1);
    });
    setCategoryDirty(true);
  };

  const handleSaveCategory = async () => {
    if (!productId.trim()) {
      toast.error("Please enter a Product ID first.");
      return;
    }

    try {
      setIsCategorySaving(true);
      const cc = String(safeCountry || "US").toUpperCase();
      await Api.put(
        `/product/${encodeURIComponent(productId)}?country=${encodeURIComponent(cc)}&currency=USD`,
        {
          categoryId: categoryId || null,
          categoryPath: normalizeCategoryPath(categoryPath),
        }
      );
      toast.success("Category updated successfully.");
      setCategoryDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update category.");
    } finally {
      setIsCategorySaving(false);
    }
  };

  const handleDataChange = (country, field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [country]: {
        ...prev[country],
        [field]: value,
      },
    }));
    setIsEditing(true);
  };


  const setSkuPricingDraft = (countryCode, skuId, patch) => {
    const cc = String(countryCode || "").toUpperCase();
    const sid = String(skuId || "").trim();
    if (!cc || !sid) return;

    setSkuPricing((prev) => ({
      ...prev,
      [cc]: {
        ...(prev?.[cc] || {}),
        [sid]: {
          ...(prev?.[cc]?.[sid] || { sku_id: sid, countryCode: cc, productId }),
          ...patch,
        },
      },
    }));
  };

  const saveSkuPricingOverride = async (countryCode, skuId) => {
    const cc = String(countryCode || "").toUpperCase();
    const sid = String(skuId || "").trim();
    if (!productId || !cc || !sid) return;

    const current = skuPricing?.[cc]?.[sid] || {};

    const normalizeNumOrNull = (v) => {
      if (v === undefined || v === null || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const buildOverride = (country) => ({
      countryCode: String(country || "").toUpperCase(),
      sku_id: sid,
      sku_price_usd: normalizeNumOrNull(current?.sku_price_usd),
      our_selling_price_usd: normalizeNumOrNull(current?.our_selling_price_usd),
    });

    const countriesToWrite = applyToAllCountries
      ? Object.keys(editingData || {}).map((c) => String(c).toUpperCase()).filter(Boolean)
      : [cc];

    const overrides = countriesToWrite.map(buildOverride);

    try {
      await Api.put(`/sku-pricing/${productId}`, { overrides });
      toast.success("SKU pricing saved.");

      // Reload overrides for the currently selected country so we stay in sync with DB.
      const res = await Api.get(`/sku-pricing/${productId}?country=${cc}`);
      const rows = res?.data?.data;
      const map = Array.isArray(rows)
        ? rows.reduce((acc, r) => {
            const key = String(r?.sku_id || "").trim();
            if (key) acc[key] = r;
            return acc;
          }, {})
        : {};
      setSkuPricing((prev) => ({ ...prev, [cc]: map }));

      setEditingSkuIndex(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save SKU pricing.");
    }
  };

  const resetSkuPricingOverride = async (countryCode, skuId) => {
    const cc = String(countryCode || "").toUpperCase();
    const sid = String(skuId || "").trim();
    if (!productId || !cc || !sid) return;

    const countriesToWrite = applyToAllCountries
      ? Object.keys(editingData || {}).map((c) => String(c).toUpperCase()).filter(Boolean)
      : [cc];

    const overrides = countriesToWrite.map((country) => ({
      countryCode: String(country || "").toUpperCase(),
      sku_id: sid,
      sku_price_usd: null,
      our_selling_price_usd: null,
    }));

    try {
      await Api.put(`/sku-pricing/${productId}`, { overrides });
      toast.success("SKU pricing reset to country markup.");

      const res = await Api.get(`/sku-pricing/${productId}?country=${cc}`);
      const rows = res?.data?.data;
      const map = Array.isArray(rows)
        ? rows.reduce((acc, r) => {
            const key = String(r?.sku_id || "").trim();
            if (key) acc[key] = r;
            return acc;
          }, {})
        : {};
      setSkuPricing((prev) => ({ ...prev, [cc]: map }));

      setEditingSkuIndex(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to reset SKU pricing.");
    }
  };

  const getProductViewUrl = (skuId) => {
    const sid = String(skuId || "").trim();
    return `${STOREFRONT_BASE_URL}/products/${productId}?sku=${encodeURIComponent(sid)}`;
  };


  const handleSaveCountriesData = async (country) => {
    if (!productId.trim()) {
      toast.error("Please enter a Product ID first.");
      return;
    }

    try {
      let ali_data;

      if (applyToAllCountries) {
        ali_data = { ...editingData };
        Object.keys(ali_data).forEach((ctry) => {
          ali_data[ctry] = {
            ...ali_data[ctry],
            ae_item_base_info_dto: { ...editingData[country]?.ae_item_base_info_dto },
            ae_item_description_dto: { ...editingData[country]?.ae_item_description_dto },
          };
        });
      } else {
        ali_data = {
          ...editingData,
          [country]: {
            ...editingData[country],
            ae_item_base_info_dto: { ...editingData[country]?.ae_item_base_info_dto },
            ae_item_description_dto: { ...editingData[country]?.ae_item_description_dto },
          },
        };
      }

      const res = await dispatch(updateAllCountriesData({
        productId,
        ali_data,
        categoryId: categoryId || null,
          categoryPath: normalizeCategoryPath(categoryPath),
      }));
      if (res?.error) {
        toast.error(res.error.message || "Failed to save country data changes.");
        return;
      }
      toast.success(applyToAllCountries ? "Country info updated for all countries." : "Country info updated successfully.");
      fetchProductData();
      setIsEditing(false);
    } catch {
      toast.error("Error saving country data changes.");
    }
  };

  const ApplyToAllCheckbox = (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={applyToAllCountries}
        onChange={() => setApplyToAllCountries((p) => !p)}
        className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-200"
      />
      <span className="text-sm font-semibold text-slate-700">Apply to all countries</span>
    </label>
  );

  const renderOverview = () => {
    const c = editingData?.[safeCountry];
    const base = c?.ae_item_base_info_dto || {};
    const desc = c?.ae_item_description_dto || {};
    const skuList = c?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o || [];
    const imgUrls = (c?.ae_multimedia_info_dto?.image_urls || "")
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);

    return (
      <div className="space-y-4">
        <Card className="p-4 md:p-5">
          <SectionHead
            title="Snapshot"
            subtitle="Read-only overview across the key product fields (from the selected country)."
            right={
              <div className="flex flex-wrap items-center gap-2">
                <Chip icon={Globe} tone="neutral">{safeCountry}</Chip>
                <Select
                  value={safeCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="w-full sm:w-auto sm:min-w-[110px]"
                >
                  {sortedAvailableCountries.map((cc) => (
                    <option key={cc} value={cc}>
                      {getCountryLabel(cc)}
                    </option>
                  ))}
                </Select>
              </div>
            }
          />

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <Label>Countries</Label>
              <p className="mt-1 text-2xl font-black tracking-tight text-slate-900">
                {stats.countriesCount} / {sortedAvailableCountries.length}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <Label>Total SKUs</Label>
              <p className="mt-1 text-2xl font-black tracking-tight text-slate-900">{stats.skuCount}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <Label>Total Images</Label>
              <p className="mt-1 text-2xl font-black tracking-tight text-slate-900">{stats.imagesCount}</p>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <Label>Assigned Category</Label>
                <p className="mt-1 text-sm font-semibold text-slate-900">{selectedCategoryName}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Choose a category to map this product in storefront/product listing.
                </p>
              </div>
              <div className="flex flex-col gap-2 w-full lg:w-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-3 gap-2 w-full">
                  <Select
                    value={categoryId}
                    onChange={(e) => handleCategorySelectionChange(e.target.value)}
                    disabled={isCategoriesLoading || isCategorySaving}
                    className="w-full min-w-[200px]"
                  >
                    <option value="">
                      {isCategoriesLoading ? "Loading categories..." : "Unassigned"}
                    </option>
                    {(categories || []).map((cat) => (
                      <option key={cat?._id} value={cat?._id}>
                        {cat?.name || "Unnamed category"}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={level1Value}
                    onChange={(e) => handleCategoryPathLevelChange(0, e.target.value)}
                    disabled={!categoryId || !level1Options.length || isCategorySaving}
                    className="w-full min-w-[180px]"
                  >
                    <option value="">{!categoryId ? "Select category first" : "Subcategory (optional)"}</option>
                    {level1Options.map((row) => (
                      <option key={`l1-${row?.name}`} value={row?.name || ""}>
                        {row?.name || "Unnamed"}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={level2Value}
                    onChange={(e) => handleCategoryPathLevelChange(1, e.target.value)}
                    disabled={!level1Value || !level2Options.length || isCategorySaving}
                    className="w-full min-w-[180px]"
                  >
                    <option value="">
                      {!level1Value ? "Select level 1 first" : "Subcategory level 2 (optional)"}
                    </option>
                    {level2Options.map((row) => (
                      <option key={`l2-${row?.name}`} value={row?.name || ""}>
                        {row?.name || "Unnamed"}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleSaveCategory}
                    disabled={isCategorySaving || isCategoriesLoading || !categoryDirty}
                    className={cx(
                      "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition-colors",
                      isCategorySaving || isCategoriesLoading || !categoryDirty
                        ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                        : "text-white bg-slate-900 hover:bg-[#D2042D]"
                    )}
                  >
                    <Save size={16} />
                    {isCategorySaving ? "Saving..." : "Save Category"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <Label>Product Title</Label>
              <p className="mt-1 text-sm font-semibold text-slate-900 break-words">
                {base?.subject || "—"}
              </p>
              <p className="mt-2 text-xs text-slate-500">View-only (edit in the Information tab).</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <Label>Product ID</Label>
              <p className="mt-1 text-sm font-semibold text-slate-900 break-words">
                {base?.product_id || productId || "—"}
              </p>
              <p className="mt-2 text-xs text-slate-500">AliExpress product identifier.</p>
            </div>
          </div>

          <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4">
            <Label>Description</Label>
            <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap break-words">
              {desc?.description || "—"}
            </p>
          </div>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          <Card className="xl:col-span-7 p-4 md:p-5">
            <SectionHead
              title="SKU Preview"
              subtitle="Read-only view of SKU count and basic pricing fields (edit in Pricing tab)."
              right={<Chip icon={Package} tone="neutral">{skuList.length} SKUs</Chip>}
            />
            <div className="mt-4 space-y-2">
              {(skuList || []).slice(0, 6).map((sku, idx) => (
                <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Label>SKU ID</Label>
                    <p className="mt-1 text-sm font-semibold text-slate-900 truncate">{sku?.sku_id || "—"}</p>
                  </div>
                  <div className="text-right">
                    <Label>Sale Price</Label>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{sku?.offer_sale_price ?? "—"}</p>
                  </div>
                </div>
              ))}
              {skuList.length > 6 ? (
                <p className="text-xs text-slate-500">Showing first 6 SKUs. Open Pricing tab for full list.</p>
              ) : null}
            </div>
          </Card>

          <Card className="xl:col-span-5 p-4 md:p-5">
            <SectionHead
              title="Images Preview"
              subtitle="Read-only preview. Image management can be added later if needed."
              right={<Chip tone="neutral">{imgUrls.length} URLs</Chip>}
            />
            {imgUrls.length ? (
              <div className="mt-4 grid grid-cols-2 gap-2">
                {imgUrls.slice(0, 6).map((url, idx) => (
                  <div key={idx} className="rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden">
                    <img
                      src={url}
                      alt={`Product ${idx + 1}`}
                      className="w-full h-28 object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "https://via.placeholder.com/300x200?text=Image+Error";
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                No images available for this country record.
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  };

  const renderInformation = () => {
    const c = editingData?.[safeCountry];
    if (!c) {
      return (
        <Card className="p-4 md:p-5">
          <SectionHead title="Information" subtitle="Load data first to edit country-specific information." />
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            No data loaded for the selected country yet.
          </div>
        </Card>
      );
    }

    return (
      <Card className="p-4 md:p-5">
        <SectionHead
          title="Information"
          subtitle="Edit product title and description for a specific country. Optionally apply to all."
          right={
            <div className="flex flex-wrap items-center gap-2">
              {ApplyToAllCheckbox}
              <Select
                value={safeCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                disabled={applyToAllCountries}
                className="w-full sm:w-auto"
              >
                {sortedAvailableCountries.map((cc) => (
                  <option key={cc} value={cc}>
                    {getCountryLabel(cc)}
                  </option>
                ))}
              </Select>
            </div>
          }
        />

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <Label>Product Title</Label>
            <div className="mt-2">
              <Input
                value={c?.ae_item_base_info_dto?.subject || ""}
                onChange={(e) =>
                  handleDataChange(safeCountry, "ae_item_base_info_dto", {
                    ...(c?.ae_item_base_info_dto || {}),
                    subject: e.target.value,
                  })
                }
                placeholder="Enter product title"
              />
            </div>
          </div>

          <div>
            <Label>Product ID (read-only)</Label>
            <div className="mt-2">
              <Input value={c?.ae_item_base_info_dto?.product_id || productId || ""} disabled />
            </div>
          </div>
        </div>

        <div className="mt-4">
          <Label>Description</Label>
          <div className="mt-2">
            <Textarea
              rows={6}
              value={c?.ae_item_description_dto?.description || ""}
              onChange={(e) =>
                handleDataChange(safeCountry, "ae_item_description_dto", {
                  ...(c?.ae_item_description_dto || {}),
                  description: e.target.value,
                })
              }
              placeholder="Enter description"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={() => handleSaveCountriesData(safeCountry)}
            className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors"
          >
            <Save size={16} />
            Save Information
          </button>
        </div>
      </Card>
    );
  };

  const renderPricing = () => {
    const c = editingData?.[safeCountry];
    const list = c?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o || [];
    const cc = String(safeCountry || "US").toUpperCase();
    const overridesMap = skuPricing?.[cc] || {};
    const markupPercent = Number(markupByCountry?.[cc] ?? 0) || 0;
    const calcMarkupPrice = (value) => {
      const base = Number(value);
      if (!Number.isFinite(base)) return "";
      return (base * (1 + markupPercent / 100)).toFixed(2);
    };

    return (
      <Card className="p-4 md:p-5">
        <SectionHead
          title="Pricing"
          subtitle="Review pricing per SKU. Provider prices are read-only."
          right={
            <div className="relative" ref={countryMenuRef}>
              <button
                type="button"
                onClick={() => setIsCountryMenuOpen((p) => !p)}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                {getCountryLabel(safeCountry)} ({safeCountry})
                <ChevronRight size={16} className={cx("transition-transform", isCountryMenuOpen && "rotate-90")} />
              </button>

              {isCountryMenuOpen ? (
                <div className="absolute right-0 z-20 mt-2 w-[min(260px,calc(100vw-2rem))] rounded-2xl border border-slate-200 bg-white shadow-lg">
                  <div className="max-h-[260px] overflow-auto py-2">
                    {sortedAvailableCountries.map((code) => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => {
                          setSelectedCountry(code);
                          setIsCountryMenuOpen(false);
                        }}
                        className={cx(
                          "flex w-full items-center justify-between px-4 py-2 text-left text-sm",
                          safeCountry === code
                            ? "bg-slate-100 text-slate-900"
                            : "text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <span>{getCountryLabel(code)}</span>
                        <span className="text-xs text-slate-500">{code}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          }
        />

        {!list.length ? (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            No SKUs found for this country.
          </div>
        ) : (
          <div className="mt-4">
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <Chip tone="neutral">Markup: {isMarkupLoading ? "Loading…" : `${markupPercent}%`}</Chip>
              <span>Markup Price shows Offer Sale Price + {markupPercent}%.</span>
            </div>

            <div className="space-y-3 md:hidden">
              {list.map((sku, idx) => {
                const isRowEditing = editingSkuIndex === idx;
                const skuImage =
                  sku?.ae_sku_property_dtos?.ae_sku_property_d_t_o?.[0]?.sku_image ||
                  sku?.sku_image ||
                  "";
                const skuId = String(sku?.sku_id || "").trim();
                const ov = skuId ? overridesMap[skuId] : undefined;
                const hasOverride =
                  ov &&
                  (ov.sku_price_usd !== undefined || ov.our_selling_price_usd !== undefined);

                const displaySkuPrice =
                  ov?.sku_price_usd !== undefined && ov?.sku_price_usd !== null && ov?.sku_price_usd !== ""
                    ? ov.sku_price_usd
                    : (sku?.sku_price ?? "");
                const displaySellingPrice =
                  ov?.our_selling_price_usd !== undefined && ov?.our_selling_price_usd !== null && ov?.our_selling_price_usd !== ""
                    ? ov.our_selling_price_usd
                    : (calcMarkupPrice(sku?.offer_sale_price) || "");

                return (
                  <div key={sku?.sku_id || idx} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        {skuImage ? (
                          <img
                            src={skuImage}
                            alt=""
                            className="h-10 w-10 rounded-xl object-cover border border-slate-200"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-xl border border-slate-200 bg-slate-50" />
                        )}
                        <div>
                          <Label>SKU ID</Label>
                          <p className="text-xs font-semibold text-slate-900 break-all">{sku?.sku_id || "—"}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-3">
                      <div>
                        <Label>SKU Attributes</Label>
                        <p className="text-xs text-slate-700 break-words">{sku?.sku_attr || "—"}</p>
                      </div>

                      <div>
                        <Label>Offer Sale Price</Label>
                        <p className="mt-2 text-sm font-semibold text-slate-900">
                          {sku?.offer_sale_price ?? "—"}
                        </p>
                      </div>

                      <div>
                        <Label>Markup Price (+{markupPercent}%)</Label>
                        <p className="mt-2 text-sm font-semibold text-slate-900">
                          {calcMarkupPrice(sku?.offer_sale_price) || "—"}
                        </p>
                      </div>

                      <div>
                        <Label>SKU Price</Label>
                        <Input
                          type="number"
                          value={displaySkuPrice}
                          onChange={(e) => setSkuPricingDraft(cc, skuId, { sku_price_usd: e.target.value })}
                          disabled={!isRowEditing}
                          className={cx(!isRowEditing && "bg-slate-100 text-slate-500 border-slate-200")}
                        />
                      </div>

                      <div>
                        <Label>Our Selling Price</Label>
                        <Input
                          type="number"
                          value={displaySellingPrice}
                          onChange={(e) => setSkuPricingDraft(cc, skuId, { our_selling_price_usd: e.target.value })}
                          disabled={!isRowEditing}
                          className={cx(!isRowEditing && "bg-slate-100 text-slate-500 border-slate-200")}
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => window.open(getProductViewUrl(skuId), "_blank", "noopener")}
                        className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[11px] font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors"
                      >
                        <ExternalLink size={14} />
                        View
                      </button>
                      <button
                        onClick={() => setEditingSkuIndex(isRowEditing ? null : idx)}
                        className={cx(
                          "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[11px] font-semibold border transition-colors",
                          isRowEditing
                            ? "border-slate-300 bg-slate-100 text-slate-700"
                            : "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200"
                        )}
                      >
                        <Pencil size={14} />
                        {isRowEditing ? "Cancel" : "Edit"}
                      </button>
                      {isRowEditing ? (
                        <>
                          <button
                            onClick={() => resetSkuPricingOverride(cc, skuId)}
                            disabled={!hasOverride}
                            className={cx(
                              "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[11px] font-semibold border transition-colors",
                              hasOverride
                                ? "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
                                : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                            )}
                          >
                            <RotateCcw size={14} />
                            Reset
                          </button>
                          <button
                            onClick={() => saveSkuPricingOverride(cc, skuId)}
                            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-[0_0_8px_rgba(16,185,129,0.35)] transition-colors"
                          >
                            <Save size={14} />
                            Save
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-[1100px] w-full bg-white text-[13px]">
                <thead className="bg-slate-100">
                  <tr className="text-left text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                    <th className="px-3 py-2">SKU Image</th>
                    <th className="px-3 py-2">SKU Attributes</th>
                    <th className="px-3 py-2">Offer Sale Price</th>
                    <th className="px-3 py-2">Markup Price (+{markupPercent}%)</th>
                    <th className="px-3 py-2">SKU Price</th>
                    <th className="px-3 py-2">Our Selling Price</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((sku, idx) => {
                    const isRowEditing = editingSkuIndex === idx;
                    const skuImage =
                      sku?.ae_sku_property_dtos?.ae_sku_property_d_t_o?.[0]?.sku_image ||
                      sku?.sku_image ||
                      "";
                    const skuId = String(sku?.sku_id || "").trim();
                    const ov = skuId ? overridesMap[skuId] : undefined;
                    const hasOverride =
                      ov &&
                      (ov.sku_price_usd !== undefined || ov.our_selling_price_usd !== undefined);

                    const displaySkuPrice =
                      ov?.sku_price_usd !== undefined && ov?.sku_price_usd !== null && ov?.sku_price_usd !== ""
                        ? ov.sku_price_usd
                        : (sku?.sku_price ?? "");
                    const displaySellingPrice =
                      ov?.our_selling_price_usd !== undefined && ov?.our_selling_price_usd !== null && ov?.our_selling_price_usd !== ""
                        ? ov.our_selling_price_usd
                        : (calcMarkupPrice(sku?.offer_sale_price) || "");

                    return (
                      <tr key={sku?.sku_id || idx} className="border-t border-slate-200">
                        <td className="px-3 py-2">
                          {skuImage ? (
                            <img
                              src={skuImage}
                              alt=""
                              className="h-9 w-9 rounded-lg object-cover border border-slate-200"
                              loading="lazy"
                            />
                          ) : (
                            <div className="h-9 w-9 rounded-lg border border-slate-200 bg-slate-50" />
                          )}
                        </td>

                        <td className="px-3 py-2 align-top">
                          <p className="text-[12px] font-semibold text-slate-900 break-words">
                            {sku?.sku_attr || "—"}
                          </p>
                          <p className="mt-1 text-[10px] text-slate-500 break-all">SKU ID: {sku?.sku_id || "—"}</p>
                        </td>

                        <td className="px-3 py-2">
                          <span className="text-sm font-semibold text-slate-900">
                            {sku?.offer_sale_price ?? "—"}
                          </span>
                        </td>

                        <td className="px-3 py-2">
                          <span className="text-sm font-semibold text-slate-900">
                            {calcMarkupPrice(sku?.offer_sale_price) || "—"}
                          </span>
                        </td>

                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            value={displaySkuPrice}
                            onChange={(e) => setSkuPricingDraft(cc, skuId, { sku_price_usd: e.target.value })}
                            disabled={!isRowEditing}
                            className={cx(
                              "min-w-[120px]",
                              !isRowEditing && "bg-slate-100 text-slate-500 border-slate-200"
                            )}
                          />
                        </td>

                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            value={displaySellingPrice}
                            onChange={(e) => setSkuPricingDraft(cc, skuId, { our_selling_price_usd: e.target.value })}
                            disabled={!isRowEditing}
                            className={cx(
                              "min-w-[140px]",
                              !isRowEditing && "bg-slate-100 text-slate-500 border-slate-200"
                            )}
                          />
                        </td>

                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => window.open(getProductViewUrl(skuId), "_blank", "noopener")}
                              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 transition-colors"
                            >
                              <ExternalLink size={16} />
                              View
                            </button>
                            <button
                              onClick={() => setEditingSkuIndex(isRowEditing ? null : idx)}
                              className={cx(
                                "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold border transition-colors",
                                isRowEditing
                                  ? "border-slate-300 bg-slate-100 text-slate-700"
                                  : "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200"
                              )}
                            >
                              <Pencil size={16} />
                              {isRowEditing ? "Cancel" : "Edit"}
                            </button>
                            {isRowEditing ? (
                              <>
                                <button
                                  onClick={() => resetSkuPricingOverride(cc, skuId)}
                                  disabled={!hasOverride}
                                  className={cx(
                                    "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold border transition-colors",
                                    hasOverride
                                      ? "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
                                      : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                                  )}
                                >
                                  <RotateCcw size={16} />
                                  Reset
                                </button>
                                <button
                                  onClick={() => saveSkuPricingOverride(cc, skuId)}
                                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-[0_0_8px_rgba(16,185,129,0.35)] transition-colors"
                                >
                                  <Save size={16} />
                                  Save
                                </button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {isSkuPricingLoading ? (
              <div className="mt-3 text-xs text-slate-500">Loading custom pricing…</div>
            ) : null}

          </div>
        )}
      </Card>
    );
  };

  const renderTabContent = () => {
    if (activeTab === "overview") return renderOverview();
    if (activeTab === "info") return renderInformation();
    if (activeTab === "pricing") return renderPricing();
    return renderOverview();
  };

  if (status === "loading") return <div className="p-6">
      <div className="flex flex-col justify-center items-center h-64 gap-3">
        <div className="w-10 h-10 border-4 border-gray-300 border-t-black rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    </div>;

  // error state
  if (productId && !selectedProduct?.productId && status === "failed") {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <Card className="p-4 md:p-5">
            <SectionHead
              title="Product Not Found"
              subtitle={`The product with ID "${productId}" could not be found.`}
              right={
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white hover:bg-slate-50"
                >
                  <ArrowLeft size={16} />
                  Go Back
                </button>
              }
            />
            <div className="mt-4 flex items-center gap-2">
              <Chip tone="bad" icon={AlertTriangle}>Not found</Chip>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D]"
              >
                <RefreshCw size={16} />
                Try Again
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white w-full">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6 md:py-8 min-w-0">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className="h-10 w-10 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center"
                title="Back"
              >
                <ArrowLeft size={18} className="text-slate-700" />
              </button>
              <div className="min-w-0">
                <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">
                  Product Editor
                </h1>
                <p className="mt-1 text-sm text-slate-600">
                  Edit multi-country product information and SKU pricing.
                </p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Chip tone="neutral" icon={Package}>
                Product ID: {productId || "—"}
              </Chip>

              {error ? (
                <Chip tone="bad" icon={AlertTriangle}>
                  {String(error)}
                </Chip>
              ) : (
                <Chip tone="good" icon={CheckCircle2}>
                  Ready
                </Chip>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="hidden lg:block">
              <Input
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                placeholder="Enter AliExpress Product ID"
                className="w-full lg:w-[280px]"
              />
            </div>

            <button
              onClick={fetchProductData}
              disabled={status === "loading"}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors whitespace-nowrap",
                status === "loading"
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
            >
              <ChevronRight size={16} />
              Load
            </button>

            <button
              onClick={handleRefreshData}
              disabled={status === "loading"}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors whitespace-nowrap",
                status === "loading"
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
            >
              <RefreshCw size={16} className={status === "loading" ? "animate-spin" : ""} />
              Refresh
            </button>

            {isEditing ? (
              <button
                onClick={handleSaveChanges}
                disabled={status === "loading"}
                className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors whitespace-nowrap"
              >
                <Save size={16} />
                Save All
              </button>
            ) : null}
          </div>

          {/* Mobile productId input */}
          <div className="lg:hidden">
            <Input
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              placeholder="Enter AliExpress Product ID"
            />
          </div>
        </div>

        {/* Tabs */}
        <Card className="mb-4 md:mb-6 p-2">
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={cx(
                    "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition-colors border",
                    active
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  )}
                >
                  <Icon size={16} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Content */}
        {renderTabContent()}
      </div>
    </div>
  );
}
